/**
 * PricingCalculatorSection
 *
 * A reusable collapsible accordion section component for the pricing calculator.
 * Displays a header with title, optional subtotal, and expand/collapse functionality.
 *
 * Features:
 * - Smooth expand/collapse animation
 * - Header shows: Section title, completion indicator, subtotal
 * - Visual indicator when section has selections
 * - Keyboard accessible (Enter/Space to toggle)
 *
 * @see src/modules/RetoolModule/components/PricingCalculatorForm.tsx - Main form
 */

import React, { useState, useCallback } from 'react';
import { Box, Typography, IconButton, Chip, alpha, useTheme } from '@mui/material';
import {
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
  Check as CheckIcon,
} from 'lucide-react';

export interface PricingCalculatorSectionProps {
  /** Section title displayed in header */
  title: string;
  /** Optional subtitle (e.g., subtotal amount) */
  subtitle?: string;
  /** Whether the section has any selections/values */
  hasSelections?: boolean;
  /** Whether the section should start expanded */
  defaultExpanded?: boolean;
  /** Controlled expanded state (optional - uses internal state if not provided) */
  expanded?: boolean;
  /** Callback when expanded state changes */
  onExpandedChange?: (expanded: boolean) => void;
  /** Children to render inside the collapsible area */
  children: React.ReactNode;
  /** Optional icon to display before the title */
  icon?: React.ReactNode;
  /** Optional badge text (e.g., "Required", "Optional") */
  badge?: string;
  /** Badge color variant */
  badgeColor?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
}

export const PricingCalculatorSection: React.FC<PricingCalculatorSectionProps> = ({
  title,
  subtitle,
  hasSelections = false,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onExpandedChange,
  children,
  icon,
  badge,
  badgeColor = 'default',
}) => {
  const theme = useTheme();
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  // Use controlled state if provided, otherwise use internal state
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;

  const handleToggle = useCallback(() => {
    const newExpanded = !expanded;
    if (isControlled && onExpandedChange) {
      onExpandedChange(newExpanded);
    } else {
      setInternalExpanded(newExpanded);
    }
  }, [expanded, isControlled, onExpandedChange]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleToggle();
      }
    },
    [handleToggle],
  );

  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        borderRadius: '12px',
        border: '1px solid',
        borderColor: isDark ? alpha(theme.palette.divider, 0.1) : alpha(theme.palette.divider, 0.3),
        backgroundColor: isDark
          ? alpha(theme.palette.background.paper, 0.4)
          : theme.palette.background.paper,
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        mb: 2,
        '&:hover': {
          borderColor: isDark
            ? alpha(theme.palette.primary.main, 0.3)
            : alpha(theme.palette.primary.main, 0.2),
        },
      }}
    >
      {/* Header - Always visible */}
      <Box
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-expanded={expanded}
        aria-controls={`section-content-${title.replace(/\s+/g, '-').toLowerCase()}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          cursor: 'pointer',
          userSelect: 'none',
          backgroundColor: expanded
            ? isDark
              ? alpha(theme.palette.primary.main, 0.08)
              : alpha(theme.palette.primary.main, 0.04)
            : 'transparent',
          transition: 'background-color 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: isDark
              ? alpha(theme.palette.primary.main, 0.12)
              : alpha(theme.palette.primary.main, 0.08),
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '-2px',
          },
        }}
      >
        {/* Left side: Icon, Title, Badge */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {icon && (
            <Box
              sx={{
                color: hasSelections ? theme.palette.primary.main : theme.palette.text.secondary,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {icon}
            </Box>
          )}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '1.1rem',
              color: theme.palette.text.primary,
            }}
          >
            {title}
          </Typography>
          {badge && (
            <Chip
              label={badge}
              size="small"
              color={badgeColor}
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 500,
              }}
            />
          )}
          {hasSelections && (
            <CheckIcon size={18} color={theme.palette.success.main} style={{ marginLeft: 4 }} />
          )}
        </Box>

        {/* Right side: Subtotal, Expand/Collapse icon */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {subtitle &&
            (() => {
              // Check if subtitle has a label:value format (e.g., "Core Subscription: $11,524.08")
              const colonIndex = subtitle.indexOf(': $');
              if (colonIndex !== -1) {
                const label = subtitle.substring(0, colonIndex + 1);
                const value = subtitle.substring(colonIndex + 2); // Skip ": " to get "$..."
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: theme.palette.text.secondary,
                        fontSize: '0.85rem',
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        fontSize: '1.15rem',
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                );
              }
              // Fallback: render as single styled text (original behavior for plain amounts)
              return (
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    fontSize: '1.15rem',
                  }}
                >
                  {subtitle}
                </Typography>
              );
            })()}
          <IconButton
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              transition: 'transform 0.2s ease-in-out',
            }}
            aria-label={expanded ? 'Collapse section' : 'Expand section'}
          >
            {expanded ? <ChevronUpIcon size={20} /> : <ChevronDownIcon size={20} />}
          </IconButton>
        </Box>
      </Box>

      {/* Content container is always rendered so the element targeted by
       * `aria-controls` exists in the DOM whether expanded or collapsed
       * (assistive tech needs a valid id reference). Visibility is toggled
       * via the `hidden` attribute — no animated transition, so we still
       * dodge the MUI Collapse height-recalc fight that originally caused
       * the stuck-expansion bug. Children stay mounted across toggles, which
       * matches MUI Collapse's default behavior and preserves any local UI
       * state inside section content. */}
      <Box
        id={`section-content-${title.replace(/\s+/g, '-').toLowerCase()}`}
        hidden={!expanded}
        sx={{
          padding: '20px',
          borderTop: '1px solid',
          borderColor: isDark
            ? alpha(theme.palette.divider, 0.08)
            : alpha(theme.palette.divider, 0.15),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default PricingCalculatorSection;
